// The recipient-picking summary exits: send-to-shop (a store mailbox) and Send in Shop (a named
// salesperson). They share the whole form — configurable inputs, the consumer summary copy — and differ
// in where the dropdown's entries come from, which consent section they record ("retailer" vs
// "salesperson"), and which endpoint receives them. NGOP-1503 parameterised the card rather than cloning
// it, for the same reason useConsentSelection exists: two copies of a GDPR-relevant send would be free to drift.

import client from "../../api/client/SdsWebApiClient";
import { RetailerEmailUserInput } from "../../api/generated";
import { AppendixPolicy } from "./consentLogic";

/** One selectable entry in an exit's recipient dropdown. */
export interface RecipientOption {
    // Positional, because neither a retailer nor a salesperson carries an id of its own.
    id: string;
    email: string;
    name: string;
    // What the dropdown shows; may carry more than the name to disambiguate two same-named people.
    label: string;
    // The DS Web Admin record this came from, for fields only one exit sends (e.g. a retailer's city).
    source: any;
}

export interface SendRecipientMailArgs {
    sessionId: string;
    recipient: RecipientOption;
    userInputs: RetailerEmailUserInput[];
    consents: string[];
    hidePdfPrice: boolean;
    siteUrl: string;
}

export interface SummaryRecipientExit {
    /** The card config's buttonAction this exit serves. */
    buttonAction: string;
    gaEvent: string;
    gaCheckoutAction: string;
    /** gui-translation key for the dropdown's label. */
    dropdownLabelKey: string;
    dropdownLabelFallback: string;
    /** The consent section this exit records (its checkboxes and consent ids) and its appendix-tick policy. */
    consentPolicy: AppendixPolicy;
    selectRecipients: (appCustomization: any) => RecipientOption[];
    gaPayload: (recipient: RecipientOption) => { [key: string]: string };
    send: (args: SendRecipientMailArgs) => Promise<void>;
}

export const SEND_TO_SHOP = "SEND_TO_SHOP";
export const SEND_TO_SALESPERSON = "SEND_TO_SALESPERSON";

// A retailer is only a valid send-to-shop target when the store opted into receiving meetings.
export const selectShopRecipients = (appCustomization: any): RecipientOption[] =>
    (appCustomization?.RetailerList?.Retailers || [])
        .filter((r: any) => r.BookAMeetingActive)
        .map((r: any, index: number) => ({
            id: index.toString(),
            email: r.BookAMeetingEmail,
            name: r.Name,
            label: r.Name,
            source: r,
        }));

// Disabled salespeople are dropped here as well as rejected by the backend allow-list, so a disabled
// person cannot even be picked. Title is the PRD's disambiguator ("Anna — Kitchens") and is optional.
export const selectSalespersonRecipients = (appCustomization: any): RecipientOption[] =>
    (appCustomization?.SalespersonList?.Salespersons || [])
        .filter((s: any) => s.Enabled)
        .map((s: any, index: number) => ({
            id: index.toString(),
            email: s.Email,
            name: s.Name,
            label: s.Title ? `${s.Name} — ${s.Title}` : s.Name,
            source: s,
        }));

/** Type-ahead filter over what the dropdown displays. A blank query keeps the list untouched. */
export const filterRecipients = (options: RecipientOption[], query: string): RecipientOption[] => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
        return options;
    }
    return options.filter(option => option.label.toLowerCase().includes(needle));
};

export const shopExit: SummaryRecipientExit = {
    buttonAction: SEND_TO_SHOP,
    gaEvent: "cta_send_to_shop",
    gaCheckoutAction: SEND_TO_SHOP,
    dropdownLabelKey: "sendToShopRetailer",
    // Every translation key the summary cards read lives in a customer's own styling.json "translations"
    // block, and no account defines the sendToShop* family today, so the fallback is what actually
    // renders. This one used to be the key name itself, which put "sendToShopRetailer" on screen.
    dropdownLabelFallback: "Store",
    consentPolicy: { section: "retailer", appendixRequiresTick: false },
    selectRecipients: selectShopRecipients,
    gaPayload: recipient => ({ shop_name: recipient.name }),
    send: async ({ sessionId, recipient, userInputs, consents, hidePdfPrice, siteUrl }) => {
        await client.sendRetailerMailPost(sessionId, {
            retailerEmail: recipient.email,
            retailerName: recipient.name,
            retailerCity: recipient.source?.City,
            userInputs,
            consents,
            hidePdfPrice,
            siteUrl,
        });
    },
};

export const salespersonExit: SummaryRecipientExit = {
    buttonAction: SEND_TO_SALESPERSON,
    gaEvent: "cta_send_to_salesperson",
    gaCheckoutAction: SEND_TO_SALESPERSON,
    dropdownLabelKey: "sendInShopSalesperson",
    dropdownLabelFallback: "Salesperson",
    consentPolicy: { section: "salesperson", appendixRequiresTick: false },
    selectRecipients: selectSalespersonRecipients,
    gaPayload: recipient => ({ salesperson_name: recipient.name }),
    // No city: the salesperson DTO deliberately omits it (the recipient is a person, not a store).
    send: async ({ sessionId, recipient, userInputs, consents, hidePdfPrice, siteUrl }) => {
        await client.sendSalespersonMailPost(sessionId, {
            salespersonEmail: recipient.email,
            salespersonName: recipient.name,
            userInputs,
            consents,
            hidePdfPrice,
            siteUrl,
        });
    },
};
